import type {DOMAttributes} from "react";
import type React from "react";
import { useMemo} from "react";

export interface Options {
    /** Move radius in pixels before this is considered a drag. */
    moveThreshold?: number;
    /** Number of milliseconds before this is considered a long press. */
    pressDelay?: number;
    /** @deprecated Unimplemented */
    preventDefault?: boolean;
    /** @deprecated Unimplemented */
    stopPropagation?: boolean;
}

const DEFAULT_OPTIONS: Options = {
    moveThreshold: Math.sqrt(50), // Chrome's threshold appears to be somewhere around here.
    pressDelay: 300,
    preventDefault: true,
    stopPropagation: false,
}

interface TouchData {
    x: number;
    y: number;
    time: number;
}

type EventHandlers<T> = Pick<DOMAttributes<T>, 'onTouchStart' | 'onTouchEnd' | 'onMouseDown' | 'onMouseUp'>
type Callback = (ev: Pick<React.PointerEvent, 'pointerType'>) => void

export default function useTap<T = Element>(callback: Callback, options?: Options): EventHandlers<T> {
    return useMemo<EventHandlers<T>>(() => {
        const opt = {...DEFAULT_OPTIONS, ...options} as Required<Options>;
        const moveThresholdSq = opt.moveThreshold ** 2;
        let data: TouchData = {
            x: 0,
            y: 0,
            time: 0,
        }

        return {
            // pointer events are not well-supported: https://github.com/facebook/react/issues/12901
            onMouseDown(ev) {
                data = {
                    time: ev.timeStamp,
                    x: ev.screenX,
                    y: ev.screenY,
                }
            },
            onMouseUp(ev) {
                const dx = ev.screenX - data.x;
                const dy = ev.screenY - data.y;
                const distSq = dx ** 2 + dy ** 2;
                const elapsed = ev.timeStamp - data.time;
                if (distSq < moveThresholdSq && elapsed < opt.pressDelay) {
                    callback({...ev, pointerType: "mouse"});
                }
            },
            onTouchStart(ev) {
                data = {
                    time: ev.timeStamp,
                    x: ev.changedTouches[0].screenX,
                    y: ev.changedTouches[0].screenY,
                }
            },
            onTouchEnd(ev) {
                const dx = ev.changedTouches[0].screenX - data.x;
                const dy = ev.changedTouches[0].screenY - data.y;
                const distSq = dx ** 2 + dy ** 2;
                const elapsed = ev.timeStamp - data.time;
                if (distSq < moveThresholdSq && elapsed < opt.pressDelay) {
                    callback({...ev, pointerType: "touch"});
                }
            },
        }
    }, [callback, options])
}
